/**
 * Minute-by-minute green/red colouring of a case block (Braden 2026-09-13).
 *
 * A case is GREEN at a moment when every role it needs has somebody PRESENT: an anesthesiologist and, in a
 * supervised room, someone in the room (the CRNA or student, or whoever relieves them). Otherwise it is RED at that
 * moment. So a case turns red when someone goes home and back to green once cover is arranged, until the next
 * person's time runs out.
 *
 * "Present" for one person:
 *   - a SHIFT-WORKER from their shift start to their shift end;
 *   - a PECKING-ORDER provider (order set) all day — they stay until sent home;
 *   - someone with no shift on file all day (benefit of the doubt, as before);
 *   - minus every span they were sent home (Off) until they were called back — see offTimes.ts.
 *
 * RELIEF TAKES OWNERSHIP of its role (Braden 2026-09-14): from the handoff on, only the reliever covers that role — the
 * team that started the case covers it until then and never takes it back, even if the reliever later goes home. Doctor
 * and in-room relief are separate: relief for the CRNA leaves the doctor on the case, and vice versa (caseRoleHolders).
 */
export type Interval = [number, number];
export interface PresenceShift { startTimeUtc?: string; endTimeUtc?: string; order?: number | null }
export interface CoverageSegment { startMs: number; endMs: number; covered: boolean }

// Mirror .schedule-block.success / .schedule-block.incomplete in App.css, so a split block matches whole ones.
export const CASE_GREEN = 'rgba(22, 163, 74, 0.7)';
export const CASE_RED = 'rgba(224, 83, 61, 0.7)';

const subtractIntervals = (base: Interval[], cuts: Interval[]): Interval[] =>
  cuts.filter(([s, e]) => e > s).reduce<Interval[]>((acc, [cs, ce]) => acc.flatMap(([s, e]): Interval[] =>
    (ce <= s || cs >= e)
      ? [[s, e]]
      : [...(cs > s ? [[s, cs] as Interval] : []), ...(ce < e ? [[ce, e] as Interval] : [])]), base);

/**
 * The relief in force for a case: whoever was assigned as relief on it, or else on the most recent EARLIER case in the
 * same room. A reliever stays in the room they relieved into, so their cover carries to that room's later cases
 * (Braden 2026-09-13) until a newer relief is assigned there, or the relief is cleared on a later case — which leaves
 * an empty entry marking where the carry ends (see clearReliefAt). `roomSchedules` are that room's cases.
 */
export const carriedReliefEntry = (
  scheduleId: string,
  roomSchedules: { scheduleId: string; startTimeUtc: string }[],
  reliefBySchedule: Record<string, string>,
): { id: string; fromScheduleId: string } | undefined => {
  const hasEntry = (id: string) => Object.prototype.hasOwnProperty.call(reliefBySchedule, id);
  const ordered = [...roomSchedules].sort((a, b) => Date.parse(a.startTimeUtc) - Date.parse(b.startTimeUtc));
  const at = ordered.findIndex(sc => sc.scheduleId === scheduleId);
  if (at < 0) return reliefBySchedule[scheduleId] ? { id: reliefBySchedule[scheduleId], fromScheduleId: scheduleId } : undefined;
  for (let i = at; i >= 0; i--) {
    const id = ordered[i].scheduleId;
    // an empty entry = the carry was cleared here
    if (hasEntry(id)) return reliefBySchedule[id] ? { id: reliefBySchedule[id], fromScheduleId: id } : undefined;
  }
  return undefined;
};

/** The reliever in force for a case (see carriedReliefEntry, which also says which case they were assigned on). */
export const carriedRelief = (
  scheduleId: string,
  roomSchedules: { scheduleId: string; startTimeUtc: string }[],
  reliefBySchedule: Record<string, string>,
): string | undefined => carriedReliefEntry(scheduleId, roomSchedules, reliefBySchedule)?.id;

/**
 * Clear the relief box on a case. Its own assignment is removed and, if a relief from an EARLIER case would still carry
 * into it, an empty "ends here" entry is left — so the box the runner just cleared stays empty, and so do the room's
 * later cases, while earlier cases keep their relief.
 */
export const clearReliefAt = (
  scheduleId: string,
  roomSchedules: { scheduleId: string; startTimeUtc: string }[],
  reliefBySchedule: Record<string, string>,
): Record<string, string> => {
  const next = { ...reliefBySchedule };
  delete next[scheduleId];
  if (carriedRelief(scheduleId, roomSchedules, next)) next[scheduleId] = '';
  return next;
};

/** When one person is present today, as epoch-ms intervals. */
export const presenceIntervals = (shift: PresenceShift | undefined, awayIntervals: Interval[] = []): Interval[] => {
  const start = shift?.startTimeUtc ? Date.parse(shift.startTimeUtc) : NaN;
  const end = shift?.endTimeUtc ? Date.parse(shift.endTimeUtc) : NaN;
  const shiftWorker = shift?.order == null && Number.isFinite(start) && Number.isFinite(end);
  return subtractIntervals([shiftWorker ? [start, end] : [-Infinity, Infinity]], awayIntervals);
};

/** Someone who can fill a role — for the whole case, or only from / until a handoff. */
export type RoleHolder = string | { id: string; fromMs?: number; toMs?: number };
/** A relief on a case: who, and when they take over (undefined = when they arrive, for a relief placed by hand). */
export interface CaseRelief { id: string; handoffMs?: number }

/** When a relief takes a case over: its recorded handoff (never before the case starts), else when the reliever arrives. */
export const reliefTakeoverMs = (relief: CaseRelief, caseStartMs: number, presenceFor: (id: string) => Interval[]): number => {
  if (relief.handoffMs !== undefined) return Math.max(caseStartMs, relief.handoffMs);
  const arrivals = presenceFor(relief.id).filter(([, e]) => e > caseStartMs).map(([s]) => s);
  return arrivals.length ? Math.max(caseStartMs, Math.min(...arrivals)) : Infinity;
};

/**
 * Who can fill each role on a case once relief is counted: [anesthesiologist, in-room]. A relief OWNS its role from the
 * handoff — the providers who started the case hold that role only until then, and never again after it. Doctor and
 * in-room relief are independent. In a room with no CRNA, the solo doctor is the one in the room, so a CRNA taking the
 * room over takes the in-room role from them.
 */
export const caseRoleHolders = (args: {
  caseStartMs: number;
  caseEndMs: number;
  doctorIds: string[];
  nurseIds: string[];
  doctorRelief?: CaseRelief;
  inRoomRelief?: CaseRelief & { isDoctor: boolean };
  presenceFor: (id: string) => Interval[];
}): RoleHolder[][] => {
  const takeoverAt = (r: CaseRelief): number => reliefTakeoverMs(r, args.caseStartMs, args.presenceFor);
  const handOver = (starters: string[], r: CaseRelief | undefined): RoleHolder[] => {
    if (!r) return starters;
    const at = takeoverAt(r);
    return [...starters.filter(id => id !== r.id).map(id => ({ id, toMs: at })), { id: r.id, fromMs: at }];
  };
  const doctorRole = handOver(args.doctorIds, args.doctorRelief);
  if (args.inRoomRelief?.isDoctor) doctorRole.push({ id: args.inRoomRelief.id, fromMs: takeoverAt(args.inRoomRelief) });
  const inRoomStarters = args.nurseIds.length ? args.nurseIds : (args.inRoomRelief ? args.doctorIds : []);
  const inRoomRole = inRoomStarters.length || args.inRoomRelief ? handOver(inRoomStarters, args.inRoomRelief) : [];
  return [doctorRole, inRoomRole];
};

/**
 * Who holds each role on a case at `atMs` — what the Board's name plates show at the time on the slider (Braden
 * 2026-09-14: "show whoever has the case at that time"). Once a relief has taken over, it replaces the providers who
 * started the case for its role; before that, the starters hold it. A doctor who goes in to relieve the CRNA holds the
 * room alone. `reliefOf` names the relief that put each id on a plate, so dragging that plate off clears the relief.
 */
export const roleHoldersAt = (args: {
  atMs: number;
  caseStartMs: number;
  doctorIds: string[];
  nurseIds: string[];
  doctorRelief?: CaseRelief;
  inRoomRelief?: CaseRelief & { isDoctor: boolean };
  presenceFor: (id: string) => Interval[];
}): { doctorIds: string[]; nurseIds: string[]; doctorTakenOver: boolean; inRoomTakenOver: boolean; reliefOf: Record<string, 'doctor' | 'inRoom'> } => {
  const takenOver = (r: CaseRelief | undefined) => !!r && args.atMs >= reliefTakeoverMs(r, args.caseStartMs, args.presenceFor);
  const doctorTakenOver = takenOver(args.doctorRelief), inRoomTakenOver = takenOver(args.inRoomRelief);
  const reliefOf: Record<string, 'doctor' | 'inRoom'> = {};
  let doctorIds = args.doctorIds, nurseIds = args.nurseIds;
  if (doctorTakenOver) { doctorIds = [args.doctorRelief!.id]; reliefOf[args.doctorRelief!.id] = 'doctor'; }
  if (inRoomTakenOver) {
    const r = args.inRoomRelief!;
    if (r.isDoctor) { doctorIds = [r.id]; nurseIds = []; } else nurseIds = [r.id];
    reliefOf[r.id] = 'inRoom';
  }
  return { doctorIds, nurseIds, doctorTakenOver, inRoomTakenOver, reliefOf };
};

/**
 * The case split into consecutive green (covered) and red spans. `roles` lists the people who can fill each role;
 * a role with nobody assigned is not required (a solo room has no in-room role).
 */
export const caseCoverageSegments = (
  caseStartMs: number,
  caseEndMs: number,
  roles: RoleHolder[][],
  presenceFor: (id: string) => Interval[],
): CoverageSegment[] => {
  const holderIntervals = (h: RoleHolder): Interval[] => {
    if (typeof h === 'string') return presenceFor(h);
    const from = h.fromMs ?? -Infinity, to = h.toMs ?? Infinity;
    return presenceFor(h.id).map(([s, e]): Interval => [Math.max(s, from), Math.min(e, to)]).filter(([s, e]) => e > s);
  };
  const required = roles.filter(holders => holders.length > 0).map(holders => holders.flatMap(holderIntervals));
  const coveredAt = (t: number) => required.every(ivs => ivs.some(([s, e]) => s <= t && t < e));
  if (!(caseEndMs > caseStartMs)) return [{ startMs: caseStartMs, endMs: caseEndMs, covered: coveredAt(caseStartMs) }];
  const edges = new Set<number>([caseStartMs, caseEndMs]);
  for (const ivs of required) for (const [s, e] of ivs) for (const t of [s, e]) if (t > caseStartMs && t < caseEndMs) edges.add(t);
  const points = [...edges].sort((a, b) => a - b);
  const segments: CoverageSegment[] = [];
  for (let i = 0; i + 1 < points.length; i++) {
    const covered = coveredAt(points[i]);
    const last = segments[segments.length - 1];
    if (last && last.covered === covered) last.endMs = points[i + 1];
    else segments.push({ startMs: points[i], endMs: points[i + 1], covered });
  }
  return segments;
};

/** Hard-edged green/red stripes along the block for a case that changes colour; undefined when it is one colour. */
export const coverageSegmentsBackground = (segments: CoverageSegment[]): string | undefined => {
  if (segments.length < 2) return undefined;
  const start = segments[0].startMs;
  const span = segments[segments.length - 1].endMs - start;
  const at = (t: number) => `${(((t - start) / span) * 100).toFixed(2)}%`;
  const stops = segments.map(sg => {
    const colour = sg.covered ? CASE_GREEN : CASE_RED;
    return `${colour} ${at(sg.startMs)}, ${colour} ${at(sg.endMs)}`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
};
